/**
 * PushNotificationsIsland — modal "Quer receber notificações?" + subscribe.
 *
 * Substitui:
 *  - `initPushNotifications()` V1 (delivery_app.html:13097) — chamado após
 *    login (LoginIsland.fireLoginHooks → window.initPushNotifications).
 *  - `askForNotificationPermission(custId, storeId)` V1 (linha 13131) —
 *    chamado em outros pontos (pós-checkout V1).
 *  - `subscribeUserToPush(custId, storeId)` V1 (linha 13163) — registra
 *    no `pushManager` e envia pra /api/system/push/subscribe.
 *
 * Expõe TUDO em `window.*` (`initPushNotifications`, `askForNotification
 * Permission`, `subscribeUserToPush`) pra LoginIsland e qualquer caller
 * V1 remanescente continuar funcionando sem mudança.
 *
 * UI: modal próprio React (sem Swal). Botões "Sim, permitir!" / "Agora não".
 * Sim → chama Notification.requestPermission() → registra subscription
 * → POST /api/system/push/subscribe. Erro de "modo silencioso" abre
 * mini-modal explicando o cadeado/sino na barra de endereço.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isAnyModalOpen, useModalOpen } from '../hooks/useAnyModalOpen';
import { t, useI18n } from '../i18n';

declare global {
  interface Window {
    VAPID_PUBLIC_KEY?: string;
    initPushNotifications?: () => void;
    askForNotificationPermission?: (customerId?: number | null, storeId?: number | null) => void;
    subscribeUserToPush?: (customerId?: number | null, storeId?: number | null) => void;
  }
}

type WinWithIds = Window & {
  STORE_ID?: number | string;
  USER?: { id?: number } | null;
};

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function resolveVapidKey(): Promise<string | null> {
  const fromWindow = window.VAPID_PUBLIC_KEY;
  if (fromWindow && fromWindow !== 'None') return fromWindow;
  try {
    const r = await fetch('/api/system/vapid-public-key');
    const d = await r.json();
    if (d && d.publicKey) return d.publicKey as string;
  } catch { /* */ }
  return null;
}

function resolveCustomerId(explicit?: number | null): number | null {
  if (explicit) return Number(explicit);
  const u = (window as WinWithIds).USER;
  if (u && u.id) return Number(u.id);
  try {
    const raw = localStorage.getItem('wabiz_user');
    if (raw) {
      const j = JSON.parse(raw);
      if (j && j.id) return Number(j.id);
    }
  } catch { /* */ }
  return null;
}

function resolveStoreId(explicit?: number | null): number | null {
  if (explicit) return Number(explicit);
  const s = (window as WinWithIds).STORE_ID;
  return s ? Number(s) : null;
}

async function doSubscribe(customerId: number | null, storeId: number | null): Promise<'ok' | 'silent' | 'error'> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'error';
  const vapid = await resolveVapidKey();
  if (!vapid) {
    console.error('[push] VAPID key ausente — backend não configurou');
    return 'error';
  }
  const reg = await navigator.serviceWorker.ready;
  if (!reg) return 'error';
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'error';
  if (permission === 'default') return 'silent';
  try {
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Uint8Array é aceito pela API real (BufferSource), TS lib mais
      // recente narrowed pro tipo. Cast pra compat sem erro.
      applicationServerKey: urlBase64ToUint8Array(vapid) as BufferSource,
    });
    const subJson = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    await fetch('/api/system/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        store_id: storeId,
        customer_id: customerId,
        user_agent: navigator.userAgent,
      }),
    });
    return 'ok';
  } catch (e) {
    console.warn('[push] subscribe erro', e);
    return 'error';
  }
}

type ModalState = null | 'ask' | 'silent-help';

export function PushNotificationsIsland() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const [modal, setModal] = useState<ModalState>(null);
  const pendingCtxRef = useRef<{ customerId: number | null; storeId: number | null } | null>(null);
  const waitTimerRef = useRef<number | null>(null);

  // Marca presença no refcount de modais — outros não devem abrir por cima
  // (BottomNav esconde, etc.). E nós mesmos checamos isAnyModalOpen antes
  // de abrir, então isso é só pra coerência do contador.
  useModalOpen(modal != null, () => handleDismiss());

  /**
   * Abre o modal pedido, MAS só se nenhum outro modal V2 estiver aberto.
   * Se estiver, faz polling leve (300ms) até desocupar (max 30s). Sem isso,
   * push poderia surgir no meio de outro fluxo do cliente.
   */
  function openWhenIdle(target: ModalState, attempt = 0) {
    if (waitTimerRef.current) {
      window.clearTimeout(waitTimerRef.current);
      waitTimerRef.current = null;
    }
    if (!isAnyModalOpen() || attempt > 100) {
      setModal(target);
      return;
    }
    waitTimerRef.current = window.setTimeout(
      () => openWhenIdle(target, attempt + 1),
      300,
    );
  }

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    if (!supported) {
      window.initPushNotifications = () => { /* unsupported */ };
      window.askForNotificationPermission = () => { /* unsupported */ };
      window.subscribeUserToPush = () => { /* unsupported */ };
      return;
    }

    // initPushNotifications — fluxo "pós-login": pergunta se ainda não inscrito.
    // Se houver QUALQUER outro modal V2 aberto (Login, Checkout, UserArea,
    // etc), espera o usuário fechá-lo antes de abrir o modal de push.
    // Sem isso, a race ServiceWorker.ready (que pode demorar segundos na
    // 1ª install) fazia push abrir ATRÁS do Login se o cliente tivesse
    // reaberto algo entre o close() do Login e a resolução do SW.
    window.initPushNotifications = async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) return;
        pendingCtxRef.current = {
          customerId: resolveCustomerId(),
          storeId: resolveStoreId(),
        };
        openWhenIdle('ask');
      } catch (e) {
        console.warn('[push] init falhou', e);
      }
    };

    // askForNotificationPermission — fluxo "pós-checkout" ou login com IDs já à mão.
    window.askForNotificationPermission = async (customerId, storeId) => {
      if (Notification.permission === 'denied') return;
      if (Notification.permission === 'granted') {
        await doSubscribe(resolveCustomerId(customerId ?? null), resolveStoreId(storeId ?? null));
        return;
      }
      pendingCtxRef.current = {
        customerId: resolveCustomerId(customerId ?? null),
        storeId: resolveStoreId(storeId ?? null),
      };
      openWhenIdle('ask');
    };

    // subscribeUserToPush — direto, sem perguntar (caller já confirmou).
    window.subscribeUserToPush = async (customerId, storeId) => {
      const result = await doSubscribe(
        resolveCustomerId(customerId ?? null),
        resolveStoreId(storeId ?? null),
      );
      if (result === 'silent') openWhenIdle('silent-help');
    };

    return () => {
      // Não removemos os handlers — outras ilhas dependem deles durante todo o ciclo.
    };
  }, []);

  const handleAccept = async () => {
    const ctx = pendingCtxRef.current || { customerId: resolveCustomerId(), storeId: resolveStoreId() };
    setModal(null);
    const result = await doSubscribe(ctx.customerId, ctx.storeId);
    if (result === 'silent') openWhenIdle('silent-help');
  };

  const handleDismiss = () => setModal(null);

  if (!modal) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100100] flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={handleDismiss} />
      <div className="relative bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
        {modal === 'ask' && (
          <>
            <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-rose-50 flex items-center justify-center">
              <i className="fas fa-bell text-3xl text-rose-600" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 text-center mb-2">
              {t('Ofertas exclusivas pra você')}
            </h3>
            <p className="text-sm text-gray-600 text-center mb-5">
              {t('Permita notificações para receber cupons relâmpago e saber quando seu pedido sair pra entrega.')}
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleAccept}
                className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm shadow-lg"
              >
                {t('Sim, permitir!')}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="w-full py-3 rounded-xl text-gray-600 font-bold text-sm hover:bg-gray-50"
              >
                {t('Agora não')}
              </button>
            </div>
          </>
        )}
        {modal === 'silent-help' && (
          <>
            <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-amber-50 flex items-center justify-center">
              <i className="fas fa-exclamation-triangle text-3xl text-amber-600" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 text-center mb-2">{t('Ativação bloqueada')}</h3>
            <p className="text-sm text-gray-600 text-center mb-5">
              {t('O navegador bloqueou a solicitação. Clique no')} <b>cadeado</b> {t('🔒 ou')} <b>sino</b> 🔕 na barra de endereço
              (topo da tela) e ative as Notificações.
            </p>
            <button
              type="button"
              onClick={handleDismiss}
              className="w-full py-3 rounded-xl bg-gray-700 hover:bg-gray-800 text-white font-bold text-sm"
            >
              {t('Entendi')}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
